import { RedoOutlined } from '@ant-design/icons';
import {
  Table,
  Paging,
  usePaging,
  useUpdate,
  EllipsisSpan,
  Button,
  Input,
  CustomFilter,
  Select,
  MultiSelect,
} from '@maxtropy/components';
import { Form } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useRequest } from 'ahooks';
import styles from './index.module.scss';
import { Link } from 'react-router-dom';
import { BatchRuleGroupPageRequest } from '../../../../api/ruleGroup';
import { IotProtocolType } from '@/shared/types';
import {
  apiV2RuleGroupDeviceAllRuleGroupPost,
  apiV2RuleRulePagePost,
  V2RuleRulePagePostResponse,
  apiV2ComprehensiveLabelListPost,
} from '@maxtropy/device-customer-apis-v2';
import { RuleGroupList } from '@/pages/Device/BatchBindRuleGroup/SelectRuleGroup/types';
import { AlarmStatus, alarmStatusDisplay, alarmStatusOptions } from '../../components/SelectRuleGroupModal/types';

export type RuleItem = Exclude<V2RuleRulePagePostResponse['list'], undefined>[number];

export interface SelectRuleGroupModalChildrenProps {
  value?: RuleItem[];
  onChange?: (val: RuleItem[]) => void;
  iotProtocol?: IotProtocolType;
  deviceTypeId?: number;
  objectModalType?: number;
  unBind?: () => void;
}

type SearchParams = Omit<BatchRuleGroupPageRequest, 'iotProtocol' | 'size' | 'page'>;

const SelectRuleGroupModalChildren: FC<SelectRuleGroupModalChildrenProps> = ({
  value,
  onChange,
  iotProtocol,
  deviceTypeId,
  objectModalType,
}) => {
  const [form] = Form.useForm();

  const pagingInfo = usePaging(100);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;

  const [searchParams, setSearchParams] = useState<SearchParams>();
  const [ruleGroups, setRuleGroups] = useState<RuleItem[]>([]);
  const [nameOptions, setNameOptions] = useState<RuleGroupList[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [updateState, updateFn] = useUpdate();

  const [loading, setLoading] = useState<boolean>(false);
  const { data: labelList } = useRequest(() => apiV2ComprehensiveLabelListPost({ id: 2 }));

  useEffect(() => {
    if (value) {
      setSelectedRowKeys(value.map(i => i.id!));
    }
  }, [value]);

  useEffect(() => {
    if (iotProtocol !== undefined) {
      setLoading(true);
      apiV2RuleRulePagePost({
        ...searchParams,
        // physicalModelId: objectModalType,
        typeId: deviceTypeId,
        // iotProtocol,
        page: pageOffset,
        size: pageSize,
      }).then(res => {
        setLoading(false);
        if (res) {
          setTotalCount(res.total ?? 0);
          res.list && setRuleGroups(res.list);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iotProtocol, pageOffset, pageSize, searchParams, setTotalCount, updateState]);

  useEffect(() => {
    apiV2RuleGroupDeviceAllRuleGroupPost({
      deviceTypeId,
      // physicalModelId,
      iotProtocol: iotProtocol as 1 | 2 | 3 | undefined,
    }).then(res => {
      res.list && setNameOptions(res.list);
    });
  }, [deviceTypeId, iotProtocol]);

  const columns = [
    {
      title: '规则名称',
      dataIndex: 'name',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '规则状态',
      dataIndex: 'status',
      ellipsis: { showTitle: true },
      render: (v: AlarmStatus) => <EllipsisSpan value={alarmStatusDisplay[v]} />,
    },
    {
      title: '规则编号',
      dataIndex: 'code',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '所属规则组',
      dataIndex: 'ruleGroupName',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '报警规则标签',
      dataIndex: 'tags',
      ellipsis: { showTitle: true },
      render: (v: string[]) => <EllipsisSpan value={v ? v.join(',') : '--'} />,
    },
  ];

  const onFinish = (value: SearchParams) => {
    setSearchParams(value);
    setPageOffset(1);
  };

  const onReset = () => {
    setSearchParams({});
    form.setFieldsValue({
      codeOrNameLike: undefined,
      ruleGroupIdList: undefined,
    });
    setPageOffset(1);
  };

  const rowSelection = {
    preserveSelectedRowKeys: true,
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[], selectedRows: RuleItem[]) => {
      setSelectedRowKeys(selectedRowKeys);
      onChange?.(selectedRows);
    },
  };
  const filters = (
    <CustomFilter form={form} colSpan={8} onFinish={onFinish} onReset={onReset}>
      <Form.Item name="codeOrNameLike" label="规则编号/名称">
        <Input placeholder="请输入编号或名称查询" />
      </Form.Item>
      <Form.Item label="规则组名称" name="ruleGroupIdList">
        <MultiSelect
          placeholder="请选择"
          showSearch
          allowClear
          options={nameOptions.map(i => ({ label: i.name, value: i.id }))}
        ></MultiSelect>
      </Form.Item>
      <Form.Item label="报警状态" name="status">
        <Select placeholder="请选择" allowClear showSearch options={alarmStatusOptions} />
      </Form.Item>
      <Form.Item label="报警规则标签" name="labelIds">
        <Select
          placeholder="请选择"
          allowClear
          mode="multiple"
          options={(labelList?.list ?? []).map(i => ({ value: i.id, label: i.name }))}
        />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <>
      {filters}
      <div className={styles.tips}>
        <span>
          <Button
            type="link"
            onClick={() => {
              setSelectedRowKeys([]);
              onChange?.([]);
            }}
          >
            全部移除
          </Button>
          <Button type="link">
            <Link to={`/device/rule/list/create`} target="_blank">
              新建规则
            </Link>
          </Button>
          <Button onClick={updateFn} icon={<RedoOutlined rotate={270} />}>
            刷新页面
          </Button>
        </span>
      </div>
      {iotProtocol === IotProtocolType.MOCKINGBIRD && (
        <div className={styles.tip}>
          提示：新增或解绑设备的报警规则组信息，需要前往网关管理页面，进行配置下发后，才可以生效。
        </div>
      )}
      <Table
        loading={loading}
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        sticky
        scroll={{ y: 300 }}
        rowKey="id"
        columns={columns}
        dataSource={ruleGroups}
      />
      <Paging pagingInfo={pagingInfo} />
    </>
  );
};

export default SelectRuleGroupModalChildren;
